import React, { useEffect, useMemo, useState } from "react";
import axios from "axios";
import { Link, useNavigate } from "react-router-dom";
import PropertyCard from "../components/PropertyCard";
import CityAutocomplete from "../components/CityAutocomplete";
import AdSenseSlot from "../components/AdSenseSlot";
import { nearestCity } from "../constants/zambianCities";
import { useTranslation } from "react-i18next";

const ADSENSE_LANDING_SLOT = process.env.REACT_APP_ADSENSE_SLOT_LANDING || "";
import { Search, Sparkles, ShieldCheck, KeyRound, Clock, Smartphone, MapPin, Compass, Flame } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const HERO_IMG = "https://images.unsplash.com/photo-1607712617949-8c993d290809?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzV8MHwxfHNlYXJjaHwxfHxsdXh1cnklMjBsb2RnZSUyMGFmcmljYXxlbnwwfHx8fDE3ODUxNDc3OTJ8MA&ixlib=rb-4.1.0&q=85";

export default function Landing() {
  const { t } = useTranslation();
  const [properties, setProperties] = useState([]);
  const [city, setCity] = useState("");
  const [nearCity, setNearCity] = useState(null); // {name, lat, lng, province}
  const [nearProperties, setNearProperties] = useState([]);
  const [locStatus, setLocStatus] = useState("idle"); // idle | detecting | ok | denied
  const [trending, setTrending] = useState({ items: [], is_cold_start: true });
  const nav = useNavigate();

  useEffect(() => {
    axios.get(`${API}/properties`).then(({ data }) => setProperties(data)).catch(() => {});
    axios.get(`${API}/properties/trending`).then(({ data }) => setTrending(data)).catch(() => {});
  }, []);

  // Detect nearest Zambian city on mount (browser geolocation → nearest match).
  // On denial or unavailability, fall back to Lusaka so the section is never empty.
  useEffect(() => {
    const applyCity = (c) => {
      setNearCity(c);
      axios
        .get(`${API}/properties`, { params: { city: c.name } })
        .then(({ data }) => setNearProperties(data.slice(0, 4)))
        .catch(() => {});
    };
    if (!("geolocation" in navigator)) {
      setLocStatus("denied");
      applyCity(nearestCity(null));
      return;
    }
    setLocStatus("detecting");
    navigator.geolocation.getCurrentPosition(
      (pos) => {
        setLocStatus("ok");
        applyCity(nearestCity({ lat: pos.coords.latitude, lng: pos.coords.longitude }));
      },
      () => {
        setLocStatus("denied");
        applyCity(nearestCity(null));
      },
      { maximumAge: 300000, timeout: 6000 }
    );
  }, []);

  const submit = (e) => {
    e.preventDefault();
    nav(`/search${city ? `?city=${encodeURIComponent(city)}` : ""}`);
  };

  const featured = properties[0];
  const rest = properties.slice(1, 5);

  const nearHeadline = useMemo(() => {
    if (!nearCity) return t("landing.nearby.nearby");
    if (locStatus === "ok") return t("landing.nearby.nearYou", { city: nearCity.name });
    return t("landing.nearby.popularIn", { city: nearCity.name });
  }, [nearCity, locStatus, t]);

  return (
    <div className="hero-gradient grain">
      {/* HERO */}
      <section className="relative overflow-hidden">
        <div className="mx-auto grid max-w-7xl gap-12 px-6 pt-16 pb-20 md:grid-cols-2 md:pt-24 md:pb-28 lg:px-8">
          <div className="flex flex-col justify-center animate-fade-up">
            <span className="chip w-fit">
              <Sparkles className="h-3 w-3" /> {t("landing.kicker")}
            </span>
            <h1 className="mt-6 font-display text-4xl font-bold leading-[1.05] tracking-tight text-slate-900 sm:text-5xl lg:text-6xl">
              {t("landing.heroTitle1")} <span className="text-copper">{t("landing.heroTitle2")}</span>
            </h1>
            <p className="mt-6 max-w-lg text-lg leading-relaxed text-slate-600">
              {t("landing.heroSubtitle")}
            </p>
            <form onSubmit={submit} className="mt-8 flex w-full max-w-md items-center gap-2 rounded-full border border-slate-200 bg-white p-2 shadow-[0_8px_30px_rgb(0,0,0,0.04)]">
              <div className="flex flex-1 items-center gap-2 px-3">
                <Search className="h-5 w-5 text-slate-400" />
                <CityAutocomplete
                  value={city}
                  onChange={setCity}
                  onSelect={(name) => nav(`/search?city=${encodeURIComponent(name)}`)}
                  onSubmit={submit}
                  placeholder={t("landing.cityPlaceholder")}
                  className="flex-1"
                  inputTestId="hero-city-input"
                  leadingIcon={false}
                />
              </div>
              <button data-testid="hero-search-btn" type="submit" className="btn-primary !py-2.5 !px-6 text-sm">{t("landing.search")}</button>
            </form>
            <div className="mt-8 flex flex-wrap items-center gap-6 text-xs uppercase tracking-widest text-slate-500">
              <span className="flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-forest" /> {t("landing.chips.verified")}</span>
              <span className="flex items-center gap-2"><Clock className="h-4 w-4 text-forest" /> {t("landing.chips.hours")}</span>
              <span className="flex items-center gap-2"><Smartphone className="h-4 w-4 text-forest" /> {t("landing.chips.momo")}</span>
            </div>
          </div>
          <div className="relative animate-fade-up" style={{ animationDelay: "120ms" }}>
            <div className="relative aspect-[4/5] overflow-hidden rounded-[2rem] border border-white/40 shadow-[0_30px_60px_rgba(0,0,0,0.15)]">
              <img src={HERO_IMG} alt="Zambian lodge" className="h-full w-full object-cover" />
              <div className="absolute inset-0 bg-gradient-to-t from-black/40 via-transparent to-transparent" />
              <div className="absolute bottom-6 left-6 right-6 rounded-2xl bg-white/95 p-4 backdrop-blur">
                <div className="text-xs uppercase tracking-widest text-slate-500">{t("landing.featuredCard.kicker")}</div>
                <div className="mt-1 font-display text-lg font-bold">{t("landing.featuredCard.title")}</div>
                <div className="mt-1 text-sm text-slate-600">{t("landing.featuredCard.meta")}</div>
              </div>
            </div>
            <div className="absolute -left-6 -bottom-6 hidden rounded-2xl bg-forest p-4 text-white shadow-xl md:block">
              <div className="text-xs uppercase tracking-widest opacity-70">{t("landing.instant.kicker")}</div>
              <div className="font-display text-2xl font-bold">{t("landing.instant.rating")}</div>
              <div className="text-xs opacity-80">{t("landing.instant.meta")}</div>
            </div>
          </div>
        </div>
      </section>

      {/* CATEGORY CHIPS */}
      <section className="mx-auto max-w-7xl px-6 lg:px-8">
        <div className="flex flex-wrap gap-2">
          {[
            { label: t("landing.categories.all"), href: "/search" },
            { label: t("landing.categories.hotels"), href: "/search?type=hotel" },
            { label: t("landing.categories.lodges"), href: "/search?type=lodge" },
            { label: t("landing.categories.guestHouses"), href: "/search?type=guest_house" },
            { label: t("landing.categories.apartments"), href: "/search?type=apartment" },
          ].map((c) => (
            <Link key={c.label} to={c.href} className="rounded-full border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:border-copper hover:text-copper">
              {c.label}
            </Link>
          ))}
        </div>
      </section>

      {/* TRENDING */}
      {trending.items.length > 0 && !trending.is_cold_start && (
        <section data-testid="trending-section" className="mx-auto max-w-7xl px-6 pt-8 lg:px-8">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-copper">
                <Flame className="h-3.5 w-3.5" /> {t("landing.trending.kicker")}
              </div>
              <h2 className="mt-2 flex items-center gap-2 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                <Flame className="h-6 w-6 text-copper" /> {t("landing.trending.title")}
              </h2>
              <p className="mt-1 text-sm text-slate-600">{t("landing.trending.subtitle")}</p>
            </div>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
            {trending.items.slice(0, 4).map((p) => (
              <div key={p.id} className="relative md:col-span-6 lg:col-span-3">
                <PropertyCard property={p} />
                <span
                  data-testid={`trending-count-${p.id}`}
                  className="absolute left-4 top-4 z-10 flex items-center gap-1 rounded-full bg-forest px-3 py-1 text-xs font-semibold text-white shadow-lg"
                  title={t("landing.trending.heartsWeek", { count: p.weekly_favourites })}
                >
                  <Flame className="h-3.5 w-3.5" /> {t("landing.trending.heartsWeek", { count: p.weekly_favourites })}
                </span>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* NEAR YOU */}
      {nearCity && nearProperties.length > 0 && (
        <section data-testid="near-you-section" className="mx-auto max-w-7xl px-6 pt-4 lg:px-8">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-3">
            <div>
              <div className="flex items-center gap-2 text-xs uppercase tracking-widest text-copper">
                <Compass className="h-3.5 w-3.5" />
                {locStatus === "ok" ? t("landing.nearby.kickerDetected") : t("landing.nearby.kickerFallback")}
              </div>
              <h2 className="mt-2 flex items-center gap-2 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">
                <MapPin className="h-6 w-6 text-forest" /> {nearHeadline}
              </h2>
              <p className="mt-1 text-sm text-slate-600">
                {locStatus === "ok"
                  ? t("landing.nearby.subtitleDetected", { province: nearCity.province })
                  : t("landing.nearby.subtitleFallback")}
              </p>
            </div>
            <Link
              data-testid="near-you-view-all"
              to={`/search?city=${encodeURIComponent(nearCity.name)}`}
              className="text-sm font-semibold text-forest hover:text-copper"
            >
              {t("landing.nearby.viewAll", { city: nearCity.name })}
            </Link>
          </div>
          <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
            {nearProperties.map((p) => <PropertyCard key={p.id} property={p} />)}
          </div>
        </section>
      )}

      {/* FEATURED GRID */}
      <section className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
        <div className="mb-8 flex items-end justify-between">
          <div>
            <div className="text-xs uppercase tracking-widest text-copper">{t("landing.curated.kicker")}</div>
            <h2 className="mt-2 font-display text-3xl font-bold tracking-tight text-slate-900 sm:text-4xl">{t("landing.curated.title")}</h2>
          </div>
          <Link to="/search" className="hidden text-sm font-semibold text-forest hover:text-copper sm:inline">{t("landing.curated.viewAll")}</Link>
        </div>
        {properties.length === 0 ? (
          <div className="rounded-3xl bg-white p-10 text-center text-slate-500">{t("landing.curated.loading")}</div>
        ) : (
          <div className="grid grid-cols-1 gap-6 md:grid-cols-12">
            {featured && <PropertyCard property={featured} featured />}
            {rest.map((p) => <PropertyCard key={p.id} property={p} />)}
          </div>
        )}
      </section>

      {/* SPONSORED — Google AdSense (only renders when slot env var is set) */}
      {ADSENSE_LANDING_SLOT && (
        <section
          data-testid="adsense-landing-section"
          className="mx-auto max-w-7xl px-6 pb-8 lg:px-8"
        >
          <div className="mb-2 text-[10px] font-semibold uppercase tracking-widest text-slate-400">
            {t("landing.sponsored")}
          </div>
          <AdSenseSlot
            slot={ADSENSE_LANDING_SLOT}
            testId="adsense-landing-slot"
            className="overflow-hidden rounded-3xl bg-white p-4 card-shadow"
          />
        </section>
      )}

      {/* HOW IT WORKS */}
      <section className="mx-auto max-w-7xl px-6 py-16 lg:px-8">
        <div className="grid gap-10 md:grid-cols-3">
          {[
            { icon: Search, title: t("landing.how.find.title"), body: t("landing.how.find.body") },
            { icon: Smartphone, title: t("landing.how.pay.title"), body: t("landing.how.pay.body") },
            { icon: KeyRound, title: t("landing.how.key.title"), body: t("landing.how.key.body") },
          ].map((s, i) => (
            <div key={s.title} className="rounded-3xl bg-white p-8 card-shadow">
              <div className="flex h-12 w-12 items-center justify-center rounded-2xl bg-copper/10 text-copper">
                <s.icon className="h-6 w-6" />
              </div>
              <div className="mt-6 text-xs uppercase tracking-widest text-slate-500">{t("landing.how.step", { n: i + 1 })}</div>
              <h3 className="mt-1 font-display text-2xl font-bold text-slate-900">{s.title}</h3>
              <p className="mt-3 text-sm leading-relaxed text-slate-600">{s.body}</p>
            </div>
          ))}
        </div>
      </section>

      {/* HOST CTA */}
      <section className="mx-auto max-w-7xl px-6 pb-24 lg:px-8">
        <div className="relative overflow-hidden rounded-[2.5rem] bg-forest p-10 md:p-16">
          <div className="relative z-10 grid gap-8 md:grid-cols-2">
            <div>
              <span className="chip !bg-white/10 !text-white">{t("landing.hostCta.badge")}</span>
              <h2 className="mt-4 font-display text-3xl font-bold text-white sm:text-4xl">{t("landing.hostCta.title")}</h2>
              <p className="mt-4 text-white/80">{t("landing.hostCta.body")}</p>
            </div>
            <div className="flex items-end justify-start md:justify-end">
              <Link to="/host" data-testid="cta-become-host" className="btn-primary text-sm">{t("landing.hostCta.button")}</Link>
            </div>
          </div>
          <div className="absolute -right-24 -top-24 h-96 w-96 rounded-full bg-copper/30 blur-3xl" />
        </div>
      </section>
    </div>
  );
}
